import LoadingSpinner from "@components/loadingSpinner";
import { showPrompt } from "@components/prompt";
import SubscriptionRenewalWarning from "@components/subscriptionRenewalWarning";
import UnlimitedIcon from "@components/unlimitedIcon";
import BlobStore from "@data/idb/blobStore";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonMenuButton,
  IonNote,
  IonProgressBar,
  IonRow,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { IAccountInfo, User } from "@models/user";
import Device from "device";
import EventBus from "eventBus";
import Globals from "globals";
import { History } from "history";
import useHasUnlimited from "hooks/util/useHasUnlimited";
import { useIonCardModalWithDismiss } from "hooks/util/useIonModalWithDismiss";
import {
  keyOutline,
  logOutOutline,
  mailOutline,
  personRemoveOutline,
  receiptOutline,
  syncOutline,
  walletOutline,
} from "ionicons/icons";
import L10n from "localization";
import { ISyncStatus, Operation } from "models/operation";
import Network from "network";
import { useCallback, useEffect, useState } from "react";
import usePDP from "unlimited/pdp";
import ChangeEmailModal from "./changeEmailModal";
import ChangePasswordModal from "./changePasswordModal";
import PaymentHistoryModal from "./paymentHistoryModal";
import ProfileNameItem from "./profileNameItem";
import SyncStatus from "./syncStatus";
import { IProfileProduct } from "./types";
import UpgradeTable from "./upgradeTable";

function getFormattedExpiration(expirationISO: string) {
  const expiration = new Date(expirationISO);
  const now = new Date();
  const diffMs = expiration.getTime() - now.getTime();
  const oneDayMs = 24 * 60 * 60 * 1000;

  // past is determined using epoch ms (time zone doesn’t affect this)
  const past = expiration.getTime() < now.getTime();

  // If the expiration is within 24 hours of now, show date and time
  let formatted: string;
  if (Math.abs(diffMs) < oneDayMs) {
    // toLocaleString() converts the UTC instant into the client's local time.
    formatted = expiration.toLocaleString();
  } else {
    // Otherwise just show the date. Note that local formatting may roll the UTC date to the previous day
    // so we enforce formatting the UTC parts correctly by using the timeZone option.
    formatted = expiration.toLocaleDateString(undefined, {
      timeZone: "UTC",
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  }
  return { formatted, past };
}

export default function Account({
  history,
}: {
  history: History;
}): JSX.Element {
  const [isSyncing, setIsSyncing] = useState<boolean>(Operation.isSyncing);
  const [email, setEmail] = useState(localStorage["AnkiApp.user.email"]);
  function handleChangeEmail() {
    setEmail(localStorage["AnkiApp.user.email"]);
  }
  const [isLoadingFeatures, setIsLoadingFeatures] = useState<boolean>(false);
  const [lastSyncStatus, setLastSyncStatus] = useState<ISyncStatus | undefined>(undefined);
  const { hasUnlimited, loading: loadingHasUnlimited } = useHasUnlimited(true);
  const [presentChangePasswordModal] = useIonCardModalWithDismiss(ChangePasswordModal, {});
  const [presentChangeEmailModal] = useIonCardModalWithDismiss(ChangeEmailModal, {
    onDismiss: handleChangeEmail,
  });

  const [products, setProducts] = useState<IProfileProduct[]>();
  useEffect(() => {
    const handleSyncBegin = () => {
      setIsSyncing(true);
    };
    const handleSyncComplete = (result: ISyncStatus | undefined) => {
      setIsSyncing(false);
      setLastSyncStatus(result);
    };
    EventBus.on("sync_begin", handleSyncBegin);
    EventBus.on("sync_complete", handleSyncComplete);
    if (!Operation.isSyncing) {
      setIsSyncing(true);
      Operation.syncOverride().finally(() => {
        setIsSyncing(false);
        setLastSyncStatus(Operation.getLastSyncStatus());
      });
    }
    return () => {
      EventBus.off("sync_complete", handleSyncComplete);
      EventBus.off("sync_begin", handleSyncBegin);
    };
  }, []);

  const [accountInfo, setAccountInfo] = useState<IAccountInfo>();

  const [presentPaymentHistoryModal] = useIonCardModalWithDismiss(PaymentHistoryModal, {
    charges: accountInfo?.charges || [],
  });

  const [loggingOut, setLoggingOut] = useState(false);

  let daysUntilExpiration: number | null = null;

  const subscription = accountInfo?.products?.find((p) => p.id !== "unlimited_lifetime");

  let expirationDisplay: string | undefined;
  let diffDays: number | undefined;

  if (subscription?.expires_at) {
    const { formatted } = getFormattedExpiration(subscription.expires_at);
    expirationDisplay = formatted;
    // Determine daysUntilExpiration using raw epoch values.
    const expirationDate = new Date(subscription.expires_at);
    const now = new Date();
    const diffMs = expirationDate.getTime() - now.getTime();
    // For display purposes, round the days up if it is still future.
    diffDays = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
    daysUntilExpiration = diffDays;
  }

  const handleLogout = () => {
    showPrompt({
      title: L10n.localize((s) => s.actions.logout),
      prompt: L10n.localize((s) => s.account.confirmLogout),
      promptType: "dangerousConfirm",
      dangerousConfirmOkButtonText: L10n.localize((s) => s.actions.logout),
      callback: async () => {
        setLoggingOut(true);

        const abort = (msg: string) => {
          showPrompt({
            title: L10n.localize((s) => s.general.attention),
            prompt: msg,
            promptType: "alert",
          });
          setLoggingOut(false);
        };

        try {
          const [operations, numPendingBlobUploads] = await Promise.all([
            Operation.unsynced(),
            BlobStore.numPendingBlobUploads(),
          ]);
          if (operations.length < 1 && numPendingBlobUploads < 1) {
            await User.logout();
            history.replace("/auth");
            return;
          }

          const keyword = "delete";

          showPrompt({
            promptType: "input",
            title: L10n.localize((s) => s.account.forceLogout),
            prompt: L10n.localize((s) => s.funcs.account.forceLogoutPrompt)(keyword),
            autoCapitalize: "off",
            callback: async (response) => {
              if (response !== keyword) {
                return abort(L10n.localize((s) => s.account.forceLogoutAborted));
              }

              setLoggingOut(true);

              await User.logout();
              history.replace("/auth");
              return;
            },
          });
          setLoggingOut(false);
        } catch {
          await User.logout();
          history.replace("/auth");
          return;
        }
      },
    });
  };

  const fetchAccountInfo = useCallback(async () => {
    try {
      setAccountInfo(
        await Network.fetch<IAccountInfo>("GET", "/users/account", {
          locale: L10n.currentLocale,
        }),
      );
    } catch {
      // ignore
    }
  }, []);

  const [showPDP] = usePDP({
    source: "profile",
    reason: undefined,
    onPurchaseComplete: fetchAccountInfo,
  });

  useEffect(() => {
    (async () => {
      fetchAccountInfo();
      try {
        setProducts(
          await Network.fetch<IProfileProduct[]>("GET", "/products", {
            locale: L10n.currentLocale,
          }),
        );
      } catch {
        // ignore
      }
    })();
  }, [fetchAccountInfo]);

  // On web, recheck unlimited status because user may have made a Stripe purchase in a different window/tab.
  useEffect(() => {
    if (Globals.platform === "web") {
      window.addEventListener("focus", fetchAccountInfo);
    }
    return () => {
      if (Globals.platform === "web") {
        window.removeEventListener("focus", fetchAccountInfo);
      }
    };
  }, [fetchAccountInfo]);

  const handleDeleteAccount = () => {
    showPrompt({
      promptType: "input",
      dangerousConfirmOkButtonText: L10n.localize((s) => s.account.delete),
      title: L10n.localize((s) => s.account.delete),
      prompt: L10n.localize((s) => s.account.confirmDeleteAccount),
      validationRegex: /.+/,
      inputType: "password",
      callback: async (pw) => {
        if (!pw) {
          return;
        }
        try {
          await User.deleteAccount(pw);
        } catch (e) {
          showPrompt({
            title: L10n.localize((s) => s.general.attention),
            prompt: L10n.localize((s) => s.account.errorDeletingAccount),
            promptType: "alert",
          });
        } finally {
          setLoggingOut(false);
        }
      },
    });
  };

  const handleUpgrade = () => {
    showPDP();
  };
  if (loggingOut) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <IonHeader translucent={true}>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>{L10n.localize((s) => s.general.account)}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen color="light">
        <IonList inset>
          <IonItem lines="none">
            <IonIcon icon={syncOutline} slot="start" />
            <IonLabel>{L10n.localize((s) => s.sync.status)}</IonLabel>
            <IonNote style={{ display: "flex", alignItems: "center" }} slot="end">
              <SyncStatus isSyncing={isSyncing} lastSyncStatus={lastSyncStatus} />
            </IonNote>
          </IonItem>
        </IonList>
        <IonList inset className="pb-8">
          <IonLoading
            isOpen={isLoadingFeatures}
            onDidDismiss={() => setIsLoadingFeatures(false)}
            message={L10n.localize((s) => s.general.dataLoadingMessage)}
          />
          {loadingHasUnlimited && <IonProgressBar type="indeterminate" />}
          {hasUnlimited && (
            <IonRow className="ion-justify-content-center">
              <IonItem lines="none">
                <UnlimitedIcon />
              </IonItem>
            </IonRow>
          )}
          {daysUntilExpiration !== null &&
            daysUntilExpiration <= 14 &&
            daysUntilExpiration >= 0 &&
            subscription?.will_auto_renew !== null && (
              <SubscriptionRenewalWarning
                daysUntilExpiration={daysUntilExpiration}
                willAutoRenew={subscription?.will_auto_renew}
                onClick={() => {
                  if (subscription?.customer_id) {
                    Device.openExternalLink(
                      `${Globals.apiEndpoint}/users/purchases/stripe/manage?c=${subscription.customer_id}`,
                    );
                  } else {
                    Device.openExternalLink("https://apps.apple.com/account/subscriptions");
                  }
                }}
              />
            )}
          {subscription && (
            <IonItem
              lines="none"
              button
              detail
              onClick={() => {
                if (subscription?.customer_id) {
                  Device.openExternalLink(
                    `${Globals.apiEndpoint}/users/purchases/stripe/manage?c=${subscription.customer_id}`,
                  );
                } else {
                  Device.openExternalLink("https://apps.apple.com/account/subscriptions");
                }
              }}
            >
              <IonIcon icon={walletOutline} slot="start" />
              <IonLabel>
                {L10n.localize((s) => s.account.manageSubscription)}
                {expirationDisplay && (
                  <IonText
                    color={
                      daysUntilExpiration !== null && daysUntilExpiration < 0 ? "danger" : "medium"
                    }
                  >
                    <p>
                      {L10n.localize((s) => s.funcs.account.subscriptionExpiresOn)(
                        expirationDisplay,
                      )}
                    </p>
                    {daysUntilExpiration !== null &&
                      daysUntilExpiration >= 0 &&
                      subscription?.will_auto_renew !== null && (
                        <p>
                          {subscription.will_auto_renew
                            ? L10n.localize((s) => s.account.autoRenewNotice)
                            : L10n.localize((s) => s.account.nonAutoRenewNotice)}
                        </p>
                      )}
                  </IonText>
                )}
              </IonLabel>
            </IonItem>
          )}
          {!loadingHasUnlimited && !hasUnlimited && products && (
            <>
              <UpgradeTable products={products} />
              <div>
                <IonButton
                  onClick={handleUpgrade}
                  expand="block"
                  size="large"
                  style={{
                    margin: "8px 12px 0 12px",
                    color: "white",
                  }}
                >
                  {L10n.localize((s) => s.account.upgradeNow)}
                </IonButton>
              </div>
            </>
          )}
        </IonList>
        <IonList inset>
          {accountInfo?.charges && accountInfo.charges.length > 0 && (
            <IonItem button detail onClick={presentPaymentHistoryModal}>
              <IonIcon icon={receiptOutline} slot="start" />
              <IonLabel>{L10n.localize((s) => s.profile.paymentHistory)}</IonLabel>
              <IonNote slot="end">
                {L10n.localize((s) => s.funcs.profile.charges)(accountInfo.charges.length)}
              </IonNote>
            </IonItem>
          )}
          <IonItem
            button
            detail
            onClick={presentChangeEmailModal}
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <IonIcon icon={mailOutline} slot="start" />

            <IonLabel style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
              {L10n.localize((s) => s.account.email)}
            </IonLabel>

            <IonNote
              slot="end"
              style={{
                flexGrow: 1,
                minWidth: 0,
                textAlign: "right",
                fontSize: "clamp(0.8rem, 2vw, 1rem)", // Shrinks text dynamically
                display: "block",
                overflow: "hidden",
              }}
            >
              {email}
            </IonNote>
          </IonItem>
          <ProfileNameItem />

          <IonItem button detail onClick={presentChangePasswordModal}>
            <IonIcon icon={keyOutline} slot="start" />
            <IonLabel>{L10n.localize((s) => s.account.changePassword)}</IonLabel>
          </IonItem>

          <IonItem button detail onClick={handleDeleteAccount}>
            <IonIcon icon={personRemoveOutline} slot="start" color="danger" />
            <IonLabel color="danger">{L10n.localize((s) => s.account.delete)}</IonLabel>
          </IonItem>
          <IonItem button detail lines="none" onClick={handleLogout}>
            <IonIcon icon={logOutOutline} slot="start" />
            <IonLabel>{L10n.localize((s) => s.actions.logout)}</IonLabel>
          </IonItem>
        </IonList>
      </IonContent>
    </>
  );
}
